import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { cmsService } from '@services/api'
import { 
  Bell, 
  Send, 
  Clock, 
  LayoutDashboard, 
  Plus, 
  Search, 
  Trash2, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  MessageSquare,
  Smartphone,
  Mail,
  Zap,
  Filter,
  User,
  Users
} from 'lucide-react'
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  Textarea,
  Select,
  SimpleGrid,
  Box,
  Card,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  FormControl,
  FormLabel,
  FormHelperText,
  useToast,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Spinner,
  Center,
  Flex,
  Divider,
} from '@chakra-ui/react'

// --- Helper Components ---
function Avatar({ size, name }: { size: string, name: string }) {
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  return (
    <Box 
      w={size === 'xs' ? '24px' : '32px'} 
      h={size === 'xs' ? '24px' : '32px'} 
      bg="blue.50" 
      color="blue.600" 
      borderRadius="full" 
      display="flex" 
      alignItems="center" 
      justifyContent="center"
      fontSize={size === 'xs' ? '10px' : '12px'}
      fontWeight="bold"
      border="1px solid"
      borderColor="blue.100"
    >
      {initials || <User size={12}/>}
    </Box>
  )
}

// --- Types ---
interface NotificationHistoryItem {
  id: string;
  userId: string | null;
  type: 'push' | 'sms' | 'email';
  recipient: string | null;
  status: 'sent' | 'delivered' | 'failed' | 'read';
  title?: string;
  body?: string;
  data?: any;
  priority: 'low' | 'normal' | 'high';
  createdAt: string;
}

interface Template {
  id: string;
  name: string;
  type: 'push' | 'sms' | 'email';
  subject?: string;
  content: string;
  active: boolean;
  variables: string[];
}

interface Segment {
  id: string;
  name: string;
  description?: string;
  userCount: number;
  active: boolean;
}

// --- Mock Data Fallbacks ---
const MOCK_TEMPLATES: Template[] = [
  { id: 'tpl_1', name: 'Welcome Message', type: 'email', subject: 'Welcome to DaladalaSmart!', content: 'Hi {{name}}, welcome aboard. Get ready for smarter commutes.', active: true, variables: ['name'] },
  { id: 'tpl_2', name: 'KYC Reminder', type: 'push', content: 'Your DaladalaSmart wallet features are locked. Please complete your KYC verification.', active: true, variables: [] },
  { id: 'tpl_3', name: 'Promo Offer', type: 'sms', content: 'Use promo code {{code}} to get 20% off your next ride! Valid for {{days}} days.', active: true, variables: ['code', 'days'] },
];

const MOCK_SEGMENTS: Segment[] = [
  { id: 'seg_1', name: 'Pending KYC', description: 'Users who have not completed wallet verification', userCount: 145, active: true },
  { id: 'seg_2', name: 'Frequent Commuters', description: 'Passengers with more than 5 bookings this month', userCount: 890, active: true },
  { id: 'seg_3', name: 'Inactive Users', description: 'Registered users with no rides in 30 days', userCount: 432, active: true },
];

// --- Main Component ---
export default function NotificationsPage() {
  const [activeTab, setActiveTab] = useState(0)
  const toast = useToast()
  const queryClient = useQueryClient()

  // 1. Send Notification Form State
  const [sendForm, setSendForm] = useState({
    targetType: 'broadcast', // broadcast | role | individual
    targetRole: 'all_users',
    userId: '',
    type: 'push' as 'push' | 'sms' | 'email',
    title: '',
    message: '',
    priority: 'normal' as 'low' | 'normal' | 'high',
    template: '',
  })
  const [isSending, setIsSending] = useState(false)

  // 2. Fetch History
  const { data: historyData, isLoading: historyLoading } = useQuery(
    ['notification-history', activeTab],
    () => cmsService.getNotificationHistory({ limit: 50 }),
    { enabled: activeTab === 1, retry: false }
  )

  // 3. Fetch Templates
  const { data: templatesData, isLoading: templatesLoading } = useQuery(
    'notification-templates',
    () => cmsService.getNotificationTemplates(),
    { retry: false }
  )

  // 4. Fetch Segments
  const { data: segmentsData, isLoading: segmentsLoading } = useQuery(
    'notification-segments',
    () => cmsService.getNotificationSegments(),
    { retry: false }
  )

  const actualTemplates = templatesData?.data?.length > 0 ? templatesData.data : MOCK_TEMPLATES
  const actualSegments = segmentsData?.data?.length > 0 ? segmentsData.data : MOCK_SEGMENTS

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!sendForm.title && !sendForm.template) {
      toast({ title: 'Validation Error', description: 'Title or Template is required', status: 'error' })
      return
    }
    if (!sendForm.message && !sendForm.template) {
        toast({ title: 'Validation Error', description: 'Message or Template is required', status: 'error' })
        return
    }

    setIsSending(true)
    try {
      if (sendForm.targetType === 'broadcast' || sendForm.targetType === 'role') {
        const payload = {
          title: sendForm.title,
          body: sendForm.message,
          targetRole: sendForm.targetType === 'broadcast' ? 'all_users' : sendForm.targetRole,
        }
        await cmsService.sendBroadcastNotification(payload)
      } else {
        const payload = {
          userIds: [sendForm.userId],
          title: sendForm.title,
          body: sendForm.message,
        }
        await cmsService.sendDirectNotification(payload)
      }

      toast({
        title: 'Notification Sent',
        description: 'Your message is being delivered.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      
      // Reset form partly
      setSendForm(prev => ({ ...prev, title: '', message: '' }))
      queryClient.invalidateQueries('notification-history')
    } catch (error: any) {
      toast({
        title: 'Send Failed',
        description: error?.message || 'Failed to send notification',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsSending(false)
    }
  }

  return (
    <VStack spacing={6} align="stretch" w="full">
      <Flex align="center" justify="space-between">
        <VStack align="start" spacing={1}>
          <Heading size="xl">Notifications</Heading>
          <Text color="gray.600">Send push notifications, SMS, and emails to your users.</Text>
        </VStack>
      </Flex>

      <Card variant="outline" bg="white" shadow="sm" borderRadius="2xl">
        <CardBody p={0}>
          <Tabs index={activeTab} onChange={setActiveTab} colorScheme="blue">
            <TabList px={6} pt={4}>
              <Tab fontWeight="medium" display="flex" alignItems="center" gap={2}><Send size={18} /> Send Notification</Tab>
              <Tab fontWeight="medium" display="flex" alignItems="center" gap={2}><Clock size={18} /> History</Tab>
              <Tab fontWeight="medium" display="flex" alignItems="center" gap={2}><LayoutDashboard size={18} /> Templates</Tab>
              <Tab fontWeight="medium" display="flex" alignItems="center" gap={2}><Users size={18} /> Segments</Tab>
            </TabList>

            <TabPanels>
              {/* --- Send Notification --- */}
              <TabPanel p={6}>
                <form onSubmit={handleSend}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                    <VStack spacing={6} align="stretch">
                      <FormControl>
                        <FormLabel fontWeight="bold">Notification Type</FormLabel>
                        <HStack spacing={4}>
                          <Button 
                            leftIcon={<Smartphone size={16}/>}
                            size="sm" 
                            variant={sendForm.type === 'push' ? 'solid' : 'outline'}
                            colorScheme="blue"
                            onClick={() => setSendForm({...sendForm, type: 'push'})}
                          >
                            Push
                          </Button>
                          <Button 
                            leftIcon={<MessageSquare size={16}/>}
                            size="sm" 
                            variant={sendForm.type === 'sms' ? 'solid' : 'outline'}
                            colorScheme="blue"
                            onClick={() => setSendForm({...sendForm, type: 'sms'})}
                            isDisabled
                          >
                            SMS
                          </Button>
                          <Button 
                            leftIcon={<Mail size={16}/>}
                            size="sm" 
                            variant={sendForm.type === 'email' ? 'solid' : 'outline'}
                            colorScheme="blue"
                            onClick={() => setSendForm({...sendForm, type: 'email'})}
                            isDisabled
                          >
                            Email
                          </Button>
                        </HStack>
                        <FormHelperText>Currently only FCM Push is fully integrated.</FormHelperText>
                      </FormControl>

                      <FormControl>
                        <FormLabel fontWeight="bold">Target Audience</FormLabel>
                        <Select 
                          value={sendForm.targetType} 
                          onChange={(e) => setSendForm({...sendForm, targetType: e.target.value})}
                          borderRadius="xl"
                        >
                          <option value="broadcast">All Users (Broadcast)</option>
                          <option value="role">Users by Role</option>
                          <option value="segment">User Segment</option>
                          <option value="individual">Specific User</option>
                        </Select>
                      </FormControl>

                      {sendForm.targetType === 'segment' && (
                        <FormControl>
                          <FormLabel>Select Segment</FormLabel>
                          <Select 
                            value={sendForm.targetRole} 
                            onChange={(e) => setSendForm({...sendForm, targetRole: e.target.value})}
                            borderRadius="xl"
                          >
                            {actualSegments.map((seg: Segment) => (
                              <option key={seg.id} value={seg.id}>{seg.name} ({seg.userCount} users)</option>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      {sendForm.targetType === 'role' && (
                        <FormControl>
                          <FormLabel>Select Role</FormLabel>
                          <Select 
                            value={sendForm.targetRole} 
                            onChange={(e) => setSendForm({...sendForm, targetRole: e.target.value})}
                            borderRadius="xl"
                          >
                            <option value="passenger">Passengers</option>
                            <option value="driver">Drivers</option>
                            <option value="conductor">Conductors</option>
                            <option value="operator">Bus Owners</option>
                          </Select>
                        </FormControl>
                      )}

                      {sendForm.targetType === 'individual' && (
                        <FormControl isRequired>
                          <FormLabel>User ID</FormLabel>
                          <Input 
                            placeholder="e.g. user_123" 
                            value={sendForm.userId}
                            onChange={(e) => setSendForm({...sendForm, userId: e.target.value})}
                            borderRadius="xl"
                          />
                        </FormControl>
                      )}

                      <FormControl>
                        <FormLabel fontWeight="bold">Priority</FormLabel>
                        <Select 
                          value={sendForm.priority} 
                          onChange={(e) => setSendForm({...sendForm, priority: e.target.value as any})}
                          borderRadius="xl"
                        >
                          <option value="low">Low</option>
                          <option value="normal">Normal</option>
                          <option value="high">High (Urgent)</option>
                        </Select>
                      </FormControl>
                    </VStack>

                    <VStack spacing={6} align="stretch">
                      <FormControl>
                        <FormLabel fontWeight="bold">Use a Template (Optional)</FormLabel>
                        <Select 
                          placeholder="Select from your templates..."
                          onChange={(e) => {
                            const tpl = actualTemplates.find((t: Template) => t.id === e.target.value)
                            if (tpl) {
                              setSendForm({ 
                                ...sendForm, 
                                template: tpl.id, 
                                title: tpl.subject || tpl.name, 
                                message: tpl.content 
                              })
                            }
                          }}
                          borderRadius="xl"
                        >
                          {actualTemplates.map((t: Template) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel fontWeight="bold">Notification Title</FormLabel>
                        <Input 
                          placeholder="e.g. Schedule Update" 
                          value={sendForm.title}
                          onChange={(e) => setSendForm({...sendForm, title: e.target.value})}
                          borderRadius="xl"
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel fontWeight="bold">Message Content</FormLabel>
                        <Textarea 
                          placeholder="Type your notification message here..." 
                          rows={6}
                          value={sendForm.message}
                          onChange={(e) => setSendForm({...sendForm, message: e.target.value})}
                          borderRadius="xl"
                        />
                      </FormControl>

                      <Button 
                        type="submit" 
                        colorScheme="blue" 
                        size="lg" 
                        leftIcon={<Zap size={20} />}
                        isLoading={isSending}
                        loadingText="Sending..."
                        borderRadius="xl"
                      >
                        Push Notification Now
                      </Button>
                    </VStack>
                  </SimpleGrid>
                </form>
              </TabPanel>

              {/* --- History --- */}
              <TabPanel p={0}>
                {historyLoading ? (
                  <Center py={20}><Spinner color="blue.500" /></Center>
                ) : (
                  <Table variant="simple">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Recipient</Th>
                        <Th>Type</Th>
                        <Th>Message</Th>
                        <Th>Status</Th>
                        <Th>Date</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(!historyData?.data || historyData.data.length === 0) ? (
                        <Tr><Td colSpan={6} textAlign="center" py={10}>No notification history found.</Td></Tr>
                      ) : (
                        historyData.data.map((item: NotificationHistoryItem) => (
                          <Tr key={item.id}>
                            <Td>
                              <HStack spacing={2}>
                                <Avatar size="xs" name={item.recipient || 'U'} />
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="sm" fontWeight="bold">{item.recipient || 'Multiple'}</Text>
                                  <Text fontSize="xs" color="gray.500">{item.userId || 'Broadcast'}</Text>
                                </VStack>
                              </HStack>
                            </Td>
                            <Td>
                              <Badge colorScheme={item.type === 'push' ? 'blue' : 'green'}>{item.type}</Badge>
                            </Td>
                            <Td maxW="300px">
                              <Text fontSize="sm" fontWeight="bold" isTruncated>{item.title}</Text>
                              <Text fontSize="xs" color="gray.600" isTruncated>{item.body}</Text>
                            </Td>
                            <Td>
                              <Badge 
                                variant="subtle" 
                                colorScheme={item.status === 'sent' ? 'green' : item.status === 'failed' ? 'red' : 'blue'}
                                borderRadius="full"
                              >
                                {item.status}
                              </Badge>
                            </Td>
                            <Td fontSize="xs" color="gray.500">
                              {new Date(item.createdAt).toLocaleString()}
                            </Td>
                            <Td>
                              <IconButton size="xs" aria-label="Delete" icon={<Trash2 size={14} />} variant="ghost" colorScheme="red" />
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                )}
              </TabPanel>

              {/* --- Templates --- */}
              <TabPanel p={6}>
                <Flex justify="space-between" mb={4}>
                  <Heading size="md">Saved Templates</Heading>
                  <Button size="sm" colorScheme="blue" leftIcon={<Plus size={16}/>}>New Template</Button>
                </Flex>
                {templatesLoading ? (
                  <Center py={10}><Spinner color="blue.500" /></Center>
                ) : (
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    {(!actualTemplates || actualTemplates.length === 0) ? (
                      <Box gridColumn="span 3" textAlign="center" py={10} color="gray.500">No templates defined yet.</Box>
                    ) : (
                      actualTemplates.map((tpl: Template) => (
                        <Card key={tpl.id} variant="outline" borderRadius="xl">
                          <CardBody>
                            <HStack justify="space-between" mb={2}>
                              <Badge colorScheme={tpl.type === 'push' ? 'blue' : 'green'}>{tpl.type}</Badge>
                              {tpl.active && <Badge colorScheme="green" variant="outline">Active</Badge>}
                            </HStack>
                            <Heading size="sm" mb={2}>{tpl.name}</Heading>
                            <Text fontSize="xs" noOfLines={3} color="gray.600">{tpl.content}</Text>
                            <Divider my={3}/>
                            <HStack spacing={1} flexWrap="wrap">
                              {tpl.variables.map(v => <Badge key={v} fontSize="10px" variant="solid">{"{{" + v + "}}"}</Badge>)}
                            </HStack>
                          </CardBody>
                        </Card>
                      ))
                    )}
                  </SimpleGrid>
                )}
              </TabPanel>

              {/* --- Segments --- */}
              <TabPanel p={6}>
                <Flex justify="space-between" mb={4}>
                  <Heading size="md">User Segments</Heading>
                  <Button size="sm" colorScheme="blue" leftIcon={<Filter size={16}/>}>Define Segment</Button>
                </Flex>
                {segmentsLoading ? (
                  <Center py={10}><Spinner color="blue.500" /></Center>
                ) : (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {(!actualSegments || actualSegments.length === 0) ? (
                      <Box gridColumn="span 2" textAlign="center" py={10} color="gray.500">No segments defined yet.</Box>
                    ) : (
                      actualSegments.map((seg: Segment) => (
                        <Card key={seg.id} variant="outline" borderRadius="xl">
                          <CardBody>
                            <HStack justify="space-between">
                              <VStack align="start" spacing={1}>
                                <Heading size="sm">{seg.name}</Heading>
                                <Text fontSize="xs" color="gray.600">{seg.description}</Text>
                                <HStack spacing={1} pt={2}>
                                  <Users size={14} color="gray" />
                                  <Text fontSize="xs" fontWeight="bold">{seg.userCount} users</Text>
                                </HStack>
                              </VStack>
                              <Button size="sm" colorScheme="blue" variant="ghost" leftIcon={<Zap size={14}/>}>Notify Segment</Button>
                            </HStack>
                          </CardBody>
                        </Card>
                      ))
                    )}
                  </SimpleGrid>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>
    </VStack>
  )
}
